import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'

export type ReportRunState = 'denied' | 'timeout' | 'cancelled' | 'overLimit' | 'invalid' | 'failed'

const CODE_MESSAGES: Readonly<Record<string, string>> = {
  QUERY_SYNTAX_ERROR: 'That query has a syntax error.',
  QUERY_PLAN_ERROR: 'This report asks for something the query engine cannot plan.',
  QUERY_NOT_AUTHORIZED: 'You do not have access to the data this report reads.',
  QUERY_EXECUTION_FAILED: 'The database could not finish this report.',
  QUERY_TIMEOUT: 'This report took too long and was stopped. Narrow it with a filter and try again.',
  QUERY_CANCELLED: 'This report was cancelled before it finished.',
  QUERY_RESOURCE_LIMIT: 'This report returns more data than one run allows. Add a filter or group the rows, then try again.',
  QUERY_FAILED: 'This report could not be run.',
  INVALID_REPORT: 'This report is not configured correctly yet.',
  INVALID_CURSOR: 'This paging session expired or the report changed. Run the report again.',
  REPORT_ANCHOR_FORBIDDEN: 'You cannot save a report at this entity.',
  REPORT_GRANT_NOT_HELD: 'You can only share a permission you hold on this report.',
  REPORT_GRANTEE_NOT_FOUND: 'That user was not found.',
  REPORT_PERMISSION_UNKNOWN: 'That is not a report permission.',
  REPORT_NOT_FOUND: 'This report was not found.',
}

export function reportErrorMessage(error: unknown, fallback = 'This report could not be run.'): string {
  return apiErrorMessage(error, fallback, { codes: CODE_MESSAGES }) + queryErrorPosition(error)
}

export function queryErrorPosition(error: unknown): string {
  if (!(error instanceof ApiError)) return ''
  const { line, column } = error.problem ?? {}
  return line !== undefined && column !== undefined ? ` (line ${line}, column ${column})` : ''
}

export const REPORT_RUN_STATE_TITLES: Record<ReportRunState, string> = {
  denied: 'You do not have access',
  timeout: 'Timed out',
  cancelled: 'Cancelled',
  overLimit: 'Too much data',
  invalid: 'Not valid',
  failed: 'Run failed',
}

export function reportRunState(error: unknown): ReportRunState {
  if (!(error instanceof ApiError)) return 'failed'
  switch (error.code) {
    case 'QUERY_TIMEOUT':
      return 'timeout'
    case 'QUERY_CANCELLED':
      return 'cancelled'
    case 'QUERY_RESOURCE_LIMIT':
      return 'overLimit'
    case 'QUERY_NOT_AUTHORIZED':
      return 'denied'
    case 'INVALID_REPORT':
    case 'INVALID_CURSOR':
    case 'QUERY_SYNTAX_ERROR':
    case 'QUERY_PLAN_ERROR':
      return 'invalid'
    default:
      return error.status === 403 ? 'denied' : 'failed'
  }
}
